/**
 * Compares two objects and returns a new object that combines `base`
 * with any empty-array-valued properties from `source` that are absent in `base`.
 *
 * Useful for preserving explicitly-empty array fields from user state
 * that proto toJSON strips (because it guards with `?.length`).
 *
 * @example
 * const base = { firstName: 'Ada', role: 'admin' };
 * const source = { firstName: 'Ada', role: 'admin', sourcesOfWealth: [], nationalities: [] };
 * transportEmptyArrayFields(base, source);
 * // => { firstName: 'Ada', role: 'admin', sourcesOfWealth: [], nationalities: [] }
 */
export function transportEmptyArrayFields<T extends Record<string, unknown>>(
  base: T,
  source: Record<string, unknown>
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...base };

  for (const key of Object.keys(source)) {
    if (Array.isArray(source[key]) && (source[key] as unknown[]).length === 0 && !(key in base)) {
      result[key] = [];
    }
  }

  return result;
}
